import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { buildOrganisationById, buildUserOrgStrictScope, isDeliveryLocationShared } from '@/utils/domain/organisationScope';

/**
 * "Which stores does the current user only see through an org sharing?" — the Vue-side source of the
 * `share` indicator (spec RG-36/RG-38), and the visual counterpart of the "Exclure magasins partagés"
 * switch. The rule itself lives in organisationScope.isDeliveryLocationShared(); this module only adds
 * the ecosVM plumbing and a cache, so no screen re-derives it on its own.
 *
 * Memoized on the deliveryLocations array identity rather than on a timer: iframeDataBridge caches its
 * deep clones by source object reference, so a new array means the legacy data actually changed (a
 * sync landed) and the set must be rebuilt. A WeakMap also lets the old entry be collected with it.
 */
const sharedIdsByDeliveryLocations = new WeakMap();

/**
 * @returns {Set<number>} ids of the delivery locations that are "Partagés" for the current user.
 */
export function getSharedDeliveryLocationIds() {
	const ecosVM = iframeDataBridge.getEcosVM();
	const deliveryLocations = ecosVM?.deliveryLocations;
	if (!Array.isArray(deliveryLocations)) return new Set();

	const cached = sharedIdsByDeliveryLocations.get(deliveryLocations);
	if (cached) return cached;

	const organisations = typeof ecosVM?.organisations === 'function' ? ecosVM.organisations() || [] : ecosVM?.organisations || [];
	const userOrgStrictScope = buildUserOrgStrictScope(Number(iframeDataBridge.getContext()?.user?.organisationId || 0), buildOrganisationById(organisations));

	const sharedIds = new Set();
	if (userOrgStrictScope) {
		for (const deliveryLocation of deliveryLocations) {
			if (isDeliveryLocationShared(deliveryLocation.organisations, userOrgStrictScope)) sharedIds.add(deliveryLocation.id);
		}
	}

	sharedIdsByDeliveryLocations.set(deliveryLocations, sharedIds);
	return sharedIds;
}

/**
 * @param {number} deliveryLocationId
 * @returns {boolean}
 */
export function isSharedDeliveryLocationId(deliveryLocationId) {
	return getSharedDeliveryLocationIds().has(deliveryLocationId);
}
